import { ANNOUNCEMENT_USER_TYPE_TAG_NAMES } from '../Model/Announcement';
import { GENERIC_COMMUNITY, isGenericCommunityId } from '../Model/Community';

const USER_TYPE_TAG_VALUES = new Set(Object.values(ANNOUNCEMENT_USER_TYPE_TAG_NAMES).map((o) => o.value));

// Sidebar sentinel for "no community filter". Lives here rather than with the context so the
// filtering helpers below stay free of context imports.
const ALL_COMMUNITIES_FILTER = 'ALL';

const POD_SLOT_PREFIX = 'pod:';
const TAG_SLOT_PREFIX = 'tag:';

// Resolves the pin slot a post competes for (one pin allowed per community).
// Posts carry their target in `communityId` - generic categories included, since those store their
// reserved id. Legacy posts predate that field, so their slot comes from the audience fields they
// do have, which keeps each pod and tag community in its own slot too.
const getPinSlotKey = ({ tagName = '', communityId = '', podId = '' } = {}) => {
  if (communityId) {
    return communityId;
  }
  if (podId) {
    return `${POD_SLOT_PREFIX}${podId}`;
  }
  if (tagName && !USER_TYPE_TAG_VALUES.has(tagName)) {
    return `${TAG_SLOT_PREFIX}${tagName}`;
  }
  return tagName || ANNOUNCEMENT_USER_TYPE_TAG_NAMES.OTHER.value;
};

// Comment totals live in two disjoint places: pod-less comments increment `comments`, pod comments
// increment `podComments[podId]`. The displayed total is therefore the sum of both.
const getAnnouncementCommentCount = ({ comments = 0, podComments = {} } = {}) => (
  Object.values(podComments).reduce((sum, count) => sum + (count || 0), comments)
);

// Feed identity of a post: siblings of one multi-community post share a key and collapse to a
// single card. Posts created before multi-select stand alone under their own id.
const getGroupKey = ({ basePostId, id } = {}) => basePostId || id;

/**
 * Display name for a community id, covering the reserved generic ids as well as community docs.
 * @param {string} communityId
 * @param {Object[]} communities - The coach's communities.
 * @param {string} [fallbackName] - Name stored on the post, used when the id resolves to nothing
 * (an archived community, or one belonging to another coach).
 * @returns {string}
 */
const getCommunityLabel = (communityId, communities = [], fallbackName = '') => {
  const generic = GENERIC_COMMUNITY.find((option) => option.id === communityId);
  if (generic) {
    return generic.name;
  }
  const community = communities.find((item) => item.id === communityId);
  return community?.name || fallbackName || communityId;
};

/**
 * Whether a post belongs to the community the sidebar is filtered to.
 * @param {Object} announcement
 * @param {string} communityFilter - A community id, or ALL_COMMUNITIES_FILTER.
 * @returns {boolean}
 */
const matchesCommunityFilter = ({ communityId, tagName } = {}, communityFilter) => {
  if (communityFilter === ALL_COMMUNITIES_FILTER) {
    return true;
  }
  if (communityId === communityFilter) {
    return true;
  }
  // Backward-compat: posts created before the community rollout carry only a legacy `tagName`
  // (no `communityId`). Match a generic community selection against that tag.
  if (!communityId && isGenericCommunityId(communityFilter)) {
    return tagName === communityFilter;
  }
  return false;
};

export {
  ALL_COMMUNITIES_FILTER,
  matchesCommunityFilter,
  getPinSlotKey,
  getAnnouncementCommentCount,
  getGroupKey,
  getCommunityLabel,
};
